import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  CheckCircle2, 
  ArrowRight, 
  ShieldCheck, 
  Clock, 
  Sparkles, 
  Phone, 
  Mail, 
  Layers, 
  Wrench, 
  Home, 
  Trees, 
  Building2, 
  Hammer, 
  Maximize2, 
  Gauge, 
  Compass, 
  Boxes 
} from 'lucide-react';
import Layout from '../components/Layout';
import ContactForm from '../components/ContactForm';
import FAQSection from '../components/FAQSection';
import { getOptimizedImageUrl } from '../lib/utils';

export default function GroundPlug() {
  const heroBg = getOptimizedImageUrl("https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/silkeborg-traeterrasse-skruefundament.jpg", { width: 1600, quality: 80 });
  const sectionImg1 = getOptimizedImageUrl("https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/jordskruer-teknisk-detalje.jpg", { width: 800, quality: 85 });
  const montageImg = getOptimizedImageUrl("https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/skruefundament-byggeplads-montering.jpg", { width: 800, quality: 85 });

  const projectTypes = [
    { 
      icon: Trees, 
      title: 'Terrasser & Uderum', 
      desc: 'Hurtig og stabil etablering af fundamentpunkter under bjælkelag og terrasser.',
      badge: 'Populært'
    },
    { 
      icon: Home, 
      title: 'Skure & Træhuse', 
      desc: 'Pålidelig bæreevne til havebygninger og skure uden støbte punktfundamenter.',
      badge: 'Effektivt'
    },
    { 
      icon: Building2, 
      title: 'Overdækninger & Carporte', 
      desc: 'Forankring og understøttelse tilpasset vindlast, geometri og underlag.',
      badge: 'Stabilt'
    },
    { 
      icon: Boxes, 
      title: 'Hegn & Stolpekonstruktioner', 
      desc: 'Præcise støttepunkter der holder stolper og lette konstruktioner i vater.',
      badge: 'Præcist'
    }
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Gennemgang af projektet',
      desc: 'Vi gennemgår projektet og den ønskede anvendelse, så udgangspunktet er helt klart.'
    },
    {
      step: '02',
      title: 'Vurdering af jordbund & forventet belastning',
      desc: 'Vi vurderer jordbund og forventet belastning ud fra konstruktionens geometri og tryk.'
    },
    {
      step: '03',
      title: 'Valg og placering af fundamenter',
      desc: 'Vi vælger og placerer de relevante fundamenter, så hver skrue yder optimal bæreevne.'
    },
    {
      step: '04',
      title: 'Montering og kontrol',
      desc: 'Vi monterer og kontrollerer løsningen efter aftale med specialudstyr og målinger.'
    }
  ];

  return (
    <Layout>
      <Helmet>
        <title>GroundPlug skruefundament til et stærkt byggegrundlag | Ring</title>
        <meta name="description" content="Med et GroundPlug skruefundament får du et solidt udgangspunkt uden den omfattende udgravning og støbning. Få levering og montering her." />
        <meta property="og:title" content="GroundPlug skruefundament til et stærkt byggegrundlag | Ring" />
        <meta property="og:description" content="Med et GroundPlug skruefundament får du et solidt udgangspunkt uden den omfattende udgravning og støbning. Få levering og montering her." />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/silkeborg-traeterrasse-skruefundament.jpg" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="GroundPlug skruefundament til et stærkt byggegrundlag | Ring" />
        <meta name="twitter:description" content="Med et GroundPlug skruefundament får du et solidt udgangspunkt uden den omfattende udgravning og støbning. Få levering og montering her." />
        <meta name="twitter:image" content="https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/silkeborg-traeterrasse-skruefundament.jpg" />
        <link rel="canonical" href="https://www.cmskruefundament.dk/groundplug" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "GroundPlug skruefundament",
            "provider": {
              "@type": "LocalBusiness",
              "name": "CM Skruefundament",
              "telephone": "+45 61 70 85 50",
              "email": "cm@skruefundamenter.dk",
              "address": {
                "@type": "PostalAddress",
                "addressCountry": "DK"
              }
            },
            "description": "Med et GroundPlug skruefundament får du et solidt udgangspunkt uden den omfattende udgravning og støbning, som følger med et traditionelt betonfundament.",
            "areaServed": "DK"
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-slate-950 text-white overflow-hidden pt-12 pb-20 lg:pt-20 lg:pb-28">
        <div className="absolute inset-0 z-0 opacity-20">
          <img 
            src={heroBg}
            alt="GroundPlug skruefundament system monteret direkte i bærende jordlag uden beton"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/90 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400 text-sm font-medium mb-6 backdrop-blur-sm">
                <Sparkles className="w-4 h-4" />
                <span>Effektiv fundering uden beton</span>
              </div>

              <h1 className="text-2xl sm:text-3xl md:text-4xl min-[992px]:text-[2.75rem] font-black uppercase tracking-tight text-white mb-6 leading-tight">
                GroundPlug skruefundament til et stærkt byggegrundlag
              </h1>

              <p className="text-lg sm:text-xl text-slate-300 mb-8 leading-relaxed font-light">
                Med et GroundPlug skruefundament får du et solidt udgangspunkt uden den omfattende udgravning og støbning, som følger med et traditionelt betonfundament. Hos <Link to="/" className="font-bold text-white underline decoration-amber-400 hover:text-amber-300 transition-colors">CM Skruefundament</Link> hjælper vi med valg af løsning, levering og montering i Jylland, på Fyn og på Sjælland.
              </p>

              <div className="flex flex-wrap gap-4 pt-2">
                <a 
                  href="#kontakt-form"
                  className="inline-flex items-center justify-center px-6 py-3.5 rounded-xl bg-amber-500 text-slate-950 font-semibold hover:bg-amber-400 transition-all shadow-lg hover:shadow-amber-500/25 active:scale-95"
                >
                  <span>Få et uforpligtende tilbud</span>
                  <ArrowRight className="w-4 h-4 ml-2" />
                </a>
                <a 
                  href="#hvad-er-groundplug"
                  className="inline-flex items-center justify-center px-6 py-3.5 rounded-xl bg-slate-800/80 text-white font-medium hover:bg-slate-700/80 border border-slate-700 transition-all backdrop-blur-sm active:scale-95"
                >
                  <span>Læs om systemet</span>
                </a>
              </div>

              {/* Quick Metrics */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-10 pt-8 border-t border-slate-800/80">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center text-amber-400 shrink-0">
                    <Clock className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white">0 timer</div>
                    <div className="text-xs text-slate-400">Ingen hærdetid</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center text-amber-400 shrink-0">
                    <Layers className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white">Minimal</div>
                    <div className="text-xs text-slate-400">Gravefri proces</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center text-amber-400 shrink-0">
                    <ShieldCheck className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white">Stærkt stål</div>
                    <div className="text-xs text-slate-400">Optager alle kræfter</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center text-amber-400 shrink-0">
                    <Compass className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white">Hele DK</div>
                    <div className="text-xs text-slate-400">Levering & montage</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Contact Form */}
            <div className="lg:col-span-5" id="kontakt-form">
              <ContactForm
                variant="hero"
                defaultService="GroundPlug"
                title="Få tilbud på GroundPlug"
                subtitle="Fortæl os om dit byggeprojekt, så rådgiver vi om valg af skruer og giver et uforpligtende tilbud."
              />
            </div>

          </div>
        </div>
      </section>

      {/* Section 1: Hvad er GroundPlug, og hvordan fungerer systemet */}
      <section id="hvad-er-groundplug" className="py-16 sm:py-24 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold uppercase tracking-wider">
                <Layers className="w-3.5 h-3.5 text-amber-500" />
                <span>Funderingssystem i stål</span>
              </div>

              <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-slate-900 leading-tight">
                Hvad er GroundPlug, og hvordan fungerer systemet?
              </h2>

              <p className="text-base sm:text-lg text-slate-700 leading-relaxed">
                GroundPlug er et system til fundering, hvor stålkomponenter føres ned i jorden og skaber stabile fundamentpunkter for konstruktionen ovenover. Løsningen kan bruges, når du vil gøre funderingsarbejdet mere enkelt og samtidig begrænse gravearbejde, jordhåndtering og brug af beton.
              </p>

              <p className="text-base sm:text-lg text-slate-700 leading-relaxed">
                GroundPlug <Link to="/jordskruer" className="font-bold text-slate-900 underline decoration-amber-500 hover:text-amber-600 transition-colors">jordskruer</Link> er de dele, der skaber forbindelsen til jorden. Når skruerne er valgt efter belastningen, jordbundsforholdene og byggeriets udformning, indgår de som et samlet GroundPlug skruefundament, der kan optage kræfterne fra den planlagte konstruktion.
              </p>

              <div className="grid sm:grid-cols-2 gap-4 pt-4">
                <div className="p-5 rounded-2xl bg-slate-50 border border-slate-200/80">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-600 flex items-center justify-center font-bold text-lg mb-3">
                    <Wrench className="w-5 h-5" />
                  </div>
                  <h4 className="font-bold text-slate-900 mb-1">Præcis kraftoverførsel</h4>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Konstruktionens laster føres direkte ned i de faste, bærende jordlag uden forstyrrelse af undergrunden.
                  </p>
                </div>

                <div className="p-5 rounded-2xl bg-slate-50 border border-slate-200/80">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-600 flex items-center justify-center font-bold text-lg mb-3">
                    <ShieldCheck className="w-5 h-5" />
                  </div>
                  <h4 className="font-bold text-slate-900 mb-1">Tilpasset byggeri</h4>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Fundamentet sammensættes efter konkrete tryk-, træk- og vandrette kræfter fra bygningens geometri.
                  </p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <figure className="relative rounded-3xl overflow-hidden shadow-2xl border border-slate-200 m-0">
                <img 
                  src={sectionImg1}
                  alt="GroundPlug stålkomponenter og jordskruer til punktfundament og bærende konstruktioner"
                  className="w-full h-full object-cover min-h-[380px] max-h-[460px]"
                  loading="lazy"
                />
                <figcaption className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-transparent to-transparent flex items-end p-6">
                  <div className="text-white">
                    <div className="text-xs font-semibold uppercase tracking-wider text-amber-400 mb-1">Stålfundering & GroundPlug</div>
                    <div className="text-lg font-bold">Stærke forbindelser i jorden uden brug af beton</div>
                    <p className="text-xs text-slate-300 mt-1">Optager tryk-, træk- og sideværts belastninger effektivt</p>
                  </div>
                </figcaption>
              </figure>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Hvorfor vælge GroundPlug skruefundamenter frem for beton */}
      <section className="py-16 sm:py-24 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 text-amber-700 text-xs font-semibold uppercase tracking-wider mb-3">
              <Sparkles className="w-3.5 h-3.5 text-amber-600" />
              <span>Fordele frem for traditionel støbning</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-slate-900 leading-tight mb-4">
              Hvorfor vælge GroundPlug skruefundamenter frem for beton?
            </h2>
            <p className="text-base sm:text-lg text-slate-700 leading-relaxed">
              GroundPlug skruefundamenter gør det muligt at etablere fundamentpunkter uden at vente på, at beton skal hærde. Det betyder, at arbejdet på den overliggende konstruktion ofte kan fortsætte med det samme, når fundamentet er korrekt monteret, kontrolleret og klar til brug.
            </p>
          </div>

          {/* 4 Feature Cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center mb-4">
                <CheckCircle2 className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">
                Mindre udgravning
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Mindre behov for udgravning og håndtering af overskudsjord på byggegrunden.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center mb-4">
                <Clock className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">
                Ingen hærdetid
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Ingen ventetid på hærdning før det videre byggeri kan påbegyndes.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center mb-4">
                <Maximize2 className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">
                Præcis placering
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Præcis placering af hvert fundamentpunkt efter projektets præcise mål.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center mb-4">
                <Layers className="w-5 h-5" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">
                Tilpasset løsning
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                En løsning der kan tilpasses konstruktion og jordbund på den enkelte grund.
              </p>
            </div>
          </div>

          <div className="bg-white p-6 sm:p-8 rounded-3xl border border-slate-200 shadow-sm">
            <p className="text-slate-700 leading-relaxed">
              Sammenlignet med beton kan systemet give en hurtigere og mere overskuelig arbejdsproces med mindre indgreb i området omkring byggeriet. Den konkrete fordel afhænger altid af projektet, adgangen til grunden, jordens egenskaber og de belastninger, fundamentet skal håndtere.
            </p>
          </div>
        </div>
      </section>

      {/* Section 3: Hvilke projekter passer GroundPlug jordskruer til */}
      <section className="py-16 sm:py-24 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold uppercase tracking-wider mb-3">
              <Wrench className="w-3.5 h-3.5 text-amber-500" />
              <span>Fleksible anvendelser</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-slate-900 leading-tight mb-4">
              Hvilke projekter passer GroundPlug jordskruer til?
            </h2>
            <p className="text-base sm:text-lg text-slate-700 leading-relaxed">
              Systemet kan være relevant til terrasser, skure, træhuse, overdækninger, hegn og andre stolpekonstruktioner. Det afgørende er ikke kun konstruktionens type, men også dens vægt, geometri og påvirkning fra vind, brug og underlag. Derfor starter en god løsning med en konkret vurdering.
            </p>
          </div>

          {/* 4 Cards Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {projectTypes.map((item, idx) => {
              const IconComponent = item.icon;
              return (
                <div 
                  key={idx}
                  className="bg-slate-50 p-6 rounded-2xl border border-slate-200 flex flex-col justify-between hover:border-amber-500/50 transition-colors group"
                >
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 rounded-xl bg-white text-slate-900 border border-slate-200 flex items-center justify-center group-hover:bg-amber-500 group-hover:text-slate-950 group-hover:border-amber-500 transition-colors">
                        <IconComponent className="w-6 h-6" />
                      </div>
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-200/80 text-slate-700">
                        {item.badge}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Subsections: Twister & Rigtigt valg */}
          <div className="grid lg:grid-cols-2 gap-8 items-stretch">
            {/* Box 1: GroundPlug Twister */}
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white p-8 rounded-3xl border border-slate-700 flex flex-col justify-between shadow-xl">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/20 text-amber-300 text-xs font-semibold mb-4 border border-amber-500/30">
                  <Hammer className="w-3.5 h-3.5" />
                  <span>GroundPlug Twister</span>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold mb-4 text-white">
                  GroundPlug Twister til terrasser og lettere konstruktioner
                </h3>
                <p className="text-slate-300 leading-relaxed mb-4">
                  GroundPlug Twister er en del af systemet fra GroundPlug og findes i varianter til forskellige opgaver. Til terrasser og udvalgte lettere konstruktioner kan Twister skabe et hurtigt fundamentpunkt, men model, længde, antal og placering skal altid passe til både jordbund og forventet belastning.
                </p>
                <p className="text-slate-300 leading-relaxed">
                  Til et mindre projekt kan det være fristende at vælge skruer alene ud fra længden. Bæreevnen kan dog ikke vurderes på målet alene. Jordtypen og monteringen har stor betydning, og ved mere krævende byggeri bør dimensionering og teknisk dokumentation indgå i beslutningen.
                </p>
              </div>
              <div className="pt-6 mt-6 border-t border-slate-700/60 flex items-center justify-between text-sm text-slate-300">
                <span className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-amber-400" /> Hurtig montage
                </span>
                <span className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-amber-400" /> Tilpasset jordbund
                </span>
              </div>
            </div>

            {/* Box 2: Det rigtige valg */}
            <div className="bg-slate-50 p-8 rounded-3xl border border-slate-200 flex flex-col justify-between">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-200 text-slate-800 text-xs font-semibold mb-4">
                  <Gauge className="w-3.5 h-3.5 text-amber-600" />
                  <span>Faglig dimensionering</span>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4">
                  Det rigtige valg afhænger af mere end længden
                </h3>
                <p className="text-slate-700 leading-relaxed mb-4">
                  Ved en terrasse handler det ofte om at skabe jævne og stabile støttepunkter for den bærende konstruktion. Ved et træhus eller en anden bygning kan kravene være større. Her skal hele lastvejen, samlingerne og fundamentets optagelse af tryk, træk og vandrette kræfter vurderes.
                </p>
                <p className="text-slate-700 leading-relaxed">
                  Vi hjælper dig med at afklare, om GroundPlug skruefundamenter passer til opgaven, eller om projektet kræver en anden type skruefundering. Den faglige vurdering er vigtig, fordi den rigtige løsning skal vælges efter det konkrete byggeri frem for et generelt standardsvar.
                </p>
              </div>
              <div className="pt-6 mt-6 border-t border-slate-200 flex items-center justify-between text-sm font-medium text-slate-700">
                <Link 
                  to="/skruefundering" 
                  className="inline-flex items-center text-amber-600 hover:text-amber-700 font-semibold gap-1"
                >
                  Læs om skruefundering generelt
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: Sådan arbejder vi med GroundPlug */}
      <section className="py-16 sm:py-24 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs font-semibold uppercase tracking-wider">
                <Compass className="w-3.5 h-3.5" />
                <span>Trin for trin proces</span>
              </div>

              <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-white leading-tight">
                Sådan arbejder vi med GroundPlug
              </h2>

              <p className="text-slate-300 text-base sm:text-lg leading-relaxed font-light">
                Hos CM Skruefundament ser vi på projektets tegninger, den planlagte konstruktion og de kendte jordbundsforhold. Derefter tager vi stilling til type, længde, antal, placering og egnet montage. På den måde bliver løsningen tilpasset det, der faktisk skal bygges.
              </p>

              <div className="space-y-4 pt-2">
                {processSteps.map((step, idx) => (
                  <div 
                    key={idx}
                    className="flex items-start gap-4 p-4 rounded-2xl bg-slate-800/80 border border-slate-700/80"
                  >
                    <div className="w-10 h-10 rounded-xl bg-amber-500 text-slate-950 font-bold flex items-center justify-center shrink-0 text-sm">
                      {step.step}
                    </div>
                    <div>
                      <h4 className="font-bold text-white text-base mb-1">{step.title}</h4>
                      <p className="text-sm text-slate-300 leading-relaxed">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-slate-300 text-sm sm:text-base leading-relaxed pt-2">
                Ved professionel montering afsætter vi fundamentpunkterne præcist og bruger egnet udstyr til nedskruningen. Kontrol under arbejdet giver et bedre grundlag for at vurdere, om skruerne opnår den nødvendige forankring, før remme, bjælker eller andre bygningsdele monteres.
              </p>
            </div>

            <div className="lg:col-span-6">
              <figure className="relative rounded-3xl overflow-hidden shadow-2xl border border-slate-800 m-0">
                <img 
                  src={montageImg}
                  alt="Professionel maskinmontering og eftervisning af bæreevne for GroundPlug skruefundament"
                  className="w-full h-full object-cover min-h-[420px] max-h-[560px]"
                  loading="lazy"
                />
                <figcaption className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/30 to-transparent flex flex-col justify-end p-8">
                  <div className="flex items-center gap-3 mb-2 text-amber-400">
                    <ShieldCheck className="w-6 h-6" />
                    <span className="font-bold text-sm uppercase tracking-wider">Kvalitetssikring & Montage</span>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">
                    Præcis nedskruning og kontrol
                  </h3>
                  <p className="text-slate-300 text-sm leading-relaxed">
                    Specialmaskiner sikrer den nødvendige forankring i de bærende jordlag uden forstyrrelse af omgivelserne.
                  </p>
                </figcaption>
              </figure>
            </div>
          </div>
        </div>
      </section>

      {/* Section 5: Køb GroundPlug, eller vælg professionel montering */}
      <section className="py-16 sm:py-24 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 text-amber-700 text-xs font-semibold uppercase tracking-wider">
                <Boxes className="w-3.5 h-3.5 text-amber-600" />
                <span>Leverandør & Montage</span>
              </div>

              <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-slate-900 leading-tight">
                Køb GroundPlug, eller vælg professionel montering
              </h2>

              <p className="text-base sm:text-lg text-slate-700 leading-relaxed">
                CM Skruefundament er både leverandør og udførende samarbejdspartner. Du kan derfor kontakte os, hvis du leder efter konkrete GroundPlug jordskruer til dit projekt, eller hvis du vil have hjælp til hele forløbet fra den første vurdering til et færdigt og kontrolleret fundament. Vi tilbyder også forankringsløsninger med <Link to="/jordanker" className="font-bold text-slate-900 underline decoration-amber-500 hover:text-amber-600 transition-colors">jordanker</Link> og et komplet sortiment i <Link to="/skruer" className="font-bold text-slate-900 underline decoration-amber-500 hover:text-amber-600 transition-colors">vores skruer</Link>. Læs mere <Link to="/om-os" className="font-bold text-slate-900 underline decoration-amber-500 hover:text-amber-600 transition-colors">om os</Link>.
              </p>

              <div className="grid sm:grid-cols-2 gap-6 pt-2">
                <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                  <div className="text-amber-500 font-bold text-xs uppercase tracking-wider mb-2">Til selvbyggere</div>
                  <h4 className="font-bold text-slate-900 text-lg mb-2">Produkter & Rådgivning</h4>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Er du selvbygger, hjælper vi dig med at forstå forskellene mellem modeller og anvendelser, så du får de rigtige produkter.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                  <div className="text-amber-500 font-bold text-xs uppercase tracking-wider mb-2">Til professionelle</div>
                  <h4 className="font-bold text-slate-900 text-lg mb-2">Dimensionering & Montage</h4>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Er du professionel, kan vi tale ind i projektets krav til dimensionering, montage og dokumentation.
                  </p>
                </div>
              </div>

              <p className="text-slate-700 text-base leading-relaxed pt-2">
                Vi lover ikke en standardløsning, før vi kender opgaven og forholdene på stedet.
              </p>
            </div>

            <div className="lg:col-span-5">
              <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-md space-y-6">
                <h3 className="text-xl font-bold text-slate-900">
                  Spørgsmål til GroundPlug?
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Vi rådgiver gerne om det rigtige valg til dit byggeprojekt. Ring eller skriv direkte til os for en faglig snak.
                </p>

                <div className="space-y-4 pt-2">
                  <a 
                    href="tel:+4561708550"
                    className="flex items-center gap-4 p-4 rounded-2xl bg-slate-50 border border-slate-200 hover:border-amber-500 hover:bg-amber-50/30 transition-all group"
                  >
                    <div className="w-12 h-12 rounded-xl bg-amber-500 text-slate-950 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                      <Phone className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-xs text-slate-500 font-medium">Ring direkte til os</div>
                      <div className="text-base font-bold text-slate-900">+45 61 70 85 50</div>
                    </div>
                  </a>

                  <a 
                    href="mailto:cm@skruefundamenter.dk"
                    className="flex items-center gap-4 p-4 rounded-2xl bg-slate-50 border border-slate-200 hover:border-amber-500 hover:bg-amber-50/30 transition-all group"
                  >
                    <div className="w-12 h-12 rounded-xl bg-slate-900 text-white flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                      <Mail className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-xs text-slate-500 font-medium">Send en e-mail</div>
                      <div className="text-base font-bold text-slate-900">cm@skruefundamenter.dk</div>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection
        badgeText="Ofte stillede spørgsmål"
        title="Spørgsmål & svar om GroundPlug"
        description="Få overblik over GroundPlug Easy Mount og Twister systemerne, bæreevne, godkendelser og lynhurtig montage uden beton."
        themeColor="amber"
        bgClass="bg-slate-50"
        items={[
          {
            question: "Hvad adskiller GroundPlug fra andre skruefundamenter på markedet?",
            answer: `GroundPlug er et patenteret dansk funderingssystem, der er særligt kendt for sine innovative serier som GroundPlug Easy Mount og GroundPlug Twister, der kombinerer letvægt med exceptionel styrke. Systemet udmærker sig ved at have specialdesignede skæreplader og rørprofiler, som gør det muligt at slå eller skrue fundamentet ned i jorden på rekordtid med en almindelig borehammer eller slagnøgle.

Det gør GroundPlug til et af de hurtigste og mest brugervenlige funderingssystemer til både private gør-det-selv folk og professionelle håndværkere.`
          },
          {
            question: "Hvad er forskellen på GroundPlug Easy Mount og GroundPlug Twister?",
            answer: `GroundPlug Easy Mount er en serie af slå-fundamenter, der hurtigt drives direkte ned i jorden med en slagboremaskine/borehammer og en specialadapter, hvilket er ideelt til lette konstruktioner som hegn, skilte, terrasser og haveskure.

GroundPlug Twister-serien består af kraftigere gevindskruer, der skrues ned med højt drejningsmoment og anvendes til projekter med højere krav til bæreevne og trækstyrke, såsom carporte, solcelleparker og større tilbygninger. Begge serier kan monteres helt uden brug af gravemaskiner eller beton.`
          },
          {
            question: "Hvilke typer byggeprojekter egner GroundPlug fundamenter sig bedst til?",
            answer: `GroundPlug systemet er yderst alsidigt og anvendes til alt fra carporte, overdækninger, hegn, legepladser, drivhuse og træterrasser til store kommercielle installationer som solcelleanlæg, vejskilte og støjskærme.

Takket være den hurtige montage uden tunge maskiner er GroundPlug særligt populært på svært tilgængelige steder, i etablerede haver hvor man vil skåne græsplænen, eller i byområder hvor der ikke er plads til store gravemaskiner.`
          },
          {
            question: "Er GroundPlug fundamenter testet og godkendt til det danske klima?",
            answer: `Ja, GroundPlug er udviklet og grundigt testet i Danmark efter gældende europæiske bygge- og ingeniørstandarder (Eurocodes). Alle komponenter er fremstillet i højstyrkestål og varmgalvaniseret, hvilket yder enestående modstandskraft over for frost, fugt og korrosive jordtyper i det omskiftelige skandinaviske klima.

Systemet leveres med dokumenterede bæreevnetabeller og ingeniørberegninger, der gør det nemt at dokumentere styrken over for bygningsmyndigheder og rådgivere.`
          },
          {
            question: "Hvor hurtigt kan man montere et GroundPlug fundament, og kan man gøre det selv?",
            answer: `Montagen af et enkelt GroundPlug punkt tager typisk kun mellem 2 og 5 minutter, når der anvendes en standard borehammer eller slagnøgle med det tilhørende monteringsværktøj. Det betyder, at et komplet fundament til eksempelvis en carport eller en stor terrasse ofte kan færdiggøres på under en enkelt formiddag af én person.

Systemet er skabt til at gøre gør-det-selv fundering let og tilgængeligt, men vi tilbyder naturligvis også professionel totalløsning med rådgivning og montage over hele landet.`
          }
        ]}
      />

      {/* Section 6: Er GroundPlug det rette valg til dit næste projekt & Contact Form */}
      <section id="tilbud" className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 text-amber-700 text-xs font-semibold uppercase tracking-wider mb-3">
              <Sparkles className="w-3.5 h-3.5 text-amber-600" />
              <span>Uforpligtende tilbud</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-slate-900 leading-tight mb-4">
              Er GroundPlug det rette valg til dit næste projekt?
            </h2>
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
              Fortæl os, hvad du vil bygge, hvor projektet ligger, og hvad du allerede ved om jordbunden. Så kan vi vurdere, om et GroundPlug skruefundament er det rette valg, og om du har brug for produkter, rådgivning eller professionel montering.
            </p>
          </div>

          <div className="max-w-3xl mx-auto bg-slate-50 p-6 sm:p-10 rounded-3xl border border-slate-200 shadow-xl">
            <ContactForm defaultService="GroundPlug" />
          </div>
        </div>
      </section>
    </Layout>
  );
}
